import React, { useContext, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { RoundButton, Heading, Button, Text } from "@foris/avocado-suite";
import { useParams, useHistory } from "react-router-dom";
import { useGetGroupsChanges } from "../../hooks/useGetGroupChanges";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import Layout from "@common/layout/Layout";
import DimensionsSearchSkeleton from "@components/dimensions-search-skeleton";
import { Header } from "@common/components";
import * as mouseflow from "@utils/mouseflow";
import ChangeRequestsTable from "../../components/change-requests-table";
import buildReport from "../../utils/reports";
import { Context } from "../../context/GroupsManagerContext";
import { ContextEDH } from "@context/ContextEDH";
import { PathParams } from "@edhtypes/general";
import { Types as NavigationTypes } from "../../context/navigation.reducer";
import scss from "./groupChangeHistory.module.scss";

/**
 * Component that displays the change history for a specific group
 * Allows viewing, filtering and exporting changes made to a group
 */
const GroupChangeHistory = () => {
  const { origin, scenario, id }: PathParams = useParams();
  const [groupChanges, loading, pageInfo, getGroupChanges] = useGetGroupsChanges(scenario, origin);
  const { state, dispatch } = useContext(Context);
  const { state: outerState } = useContext(ContextEDH);
  const [internalPageSize, setInternalPageSize] = useState(5);
  const [columns, setColumns] = useState([]);

  const history = useHistory();
  const { groupsManagerUrl, adaptedHomeUrl } = useNavigationUrl();

  const { t } = useTranslation();

  const urlParams = useMemo(() => new URLSearchParams(history.location.search), [history]);

  const isReferent = urlParams.get("isReferent") === "true";
  const linkId = urlParams.get("linkId");
  const clientCode = urlParams.get("code");

  /**
   * Requests group changes with current pagination and filters
   * Applies specific filters based on whether the user is a referent or not
   */
  const requestGroupChanges = (page: number) => {
    const variables = {
      isGrouped: {
        is: false,
      },
    };

    if (isReferent && linkId) {
      variables["linkId"] = { eq: linkId };
    }

    getGroupChanges({
      page,
      pageSize: internalPageSize,
      owned: false,
      groupId: !isReferent ? id : "",
      orderBy: state?.tableFilters?.orderBy,
      searchBy: state?.tableFilters?.searchBy,
      externalFields: variables,
    });
  };

  useEffect(() => {
    requestGroupChanges(1);
  }, [internalPageSize]);

  /**
   * Handles backward navigation using the navigation history
   * If there's no history, redirects to the main group management view
   */
  const navigateBackwards = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch({ type: NavigationTypes.SetForceFetching, payload: true });

    if ((state?.navigation?.locationStack?.length ?? 0) === 0) {
      history.push(groupsManagerUrl());
    } else {
      dispatch({ type: NavigationTypes.PopFromLocationStack });
      const locationStack = state?.navigation?.locationStack ?? [];
      const url = locationStack[locationStack.length - 1]?.view;
      history.push(url);
    }
  };

  const totalChanges = pageInfo?.total ?? 0;

  const changesTitleWithCount =
    totalChanges !== 1
      ? `${totalChanges} ${t("group-management.header.title-plural")}`
      : `${totalChanges} ${t("group-management.header.title")}`;

  const groupTitleWithCount = `${t("group-management.back-to-view.title")} ${clientCode ?? id}`;
  const lastUpdateDateTime = dayjs().format("DD/MM/YYYY HH:mm");

  // Refreshes data and resets to first page
  const handleUpdateView = () => {
    requestGroupChanges(1);
  };

  /**
   * Generates and downloads a report with the change history
   * Logs the action in Mouseflow for behavior analysis
   */
  const handleDownloadReport = () => {
    const reportLink = buildReport("changeHistory", {
      groupId: id,
      isReferent,
      linkId: linkId as unknown as number,
      context: { origin, scenario },
    });
    mouseflow.actionTag(
      "action_groups_manager_change_history_report",
      outerState?.base?.isMouseflowEnabled,
    );
    window.open(reportLink, "_blank");
  };

  return (
    <Layout contextSearch>
      <Header
        title={t("group-management.title")}
        breadcrumbItems={[
          {
            title: t("group-management.breadcrumb.prev"),
            path: adaptedHomeUrl(),
          },
          {
            title: t("group-management.breadcrumb.current"),
          },
        ]}
      />
      {loading ? (
        <div className={scss.groupChangeHistory}>
          <DimensionsSearchSkeleton columns={columns.length} rows={internalPageSize} />
        </div>
      ) : (
        <div className={scss.groupChangeHistory}>
          <div className={scss.groupChangeHistory_backToView}>
            <RoundButton onClick={navigateBackwards} icon="chevron-left" variant="ghost" />
            <Heading type="h2">{groupTitleWithCount}</Heading>
          </div>

          <header className={scss.groupChangeHistory_header}>
            <Heading type="h3" className={scss.title}>
              {changesTitleWithCount}
            </Heading>
            <div className={scss.container}>
              <Text weight="medium" className={scss.container_label}>
                {t("group-management.header.last-update")}:{" "}
                <span className={scss.textHour}>{lastUpdateDateTime}</span>
              </Text>
              <div className={scss.container_buttonGroup}>
                <Button size="sm" onClick={handleUpdateView} variant="ghost" leftIcon="refresh">
                  {t("group-management.header.btn-update-view")}
                </Button>
                <Button
                  size="sm"
                  onClick={handleDownloadReport}
                  variant="ghost"
                  leftIcon="download"
                >
                  {t("group-management.header.btn-download-report")}
                </Button>
              </div>
            </div>
          </header>
          <ChangeRequestsTable
            setColumns={setColumns}
            groupChanges={groupChanges ?? []}
            pageInfo={pageInfo}
            requestGroupCapacityChangeDetails={requestGroupChanges}
            pageSize={internalPageSize}
            onChangeSizePerPage={setInternalPageSize}
          />
        </div>
      )}
    </Layout>
  );
};

export default GroupChangeHistory;
